import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from './pages/LoginPage'
import { ClientDashboard } from './pages/ClientDashboard'
import { CustomerHomePage } from './pages/CustomerHomePage'
import { TripwiresPage } from './pages/TripwiresPage'
import { ConciergePage } from './pages/ConciergePage'
import { AnalystDashboard } from './pages/AnalystDashboard'
import { EscalationsPage } from './pages/EscalationsPage'
import { CaseDetailPage } from './pages/CaseDetailPage'
import { AdminDashboard } from './pages/AdminDashboard'
import { OnboardFamilyPage } from './pages/OnboardFamilyPage'
// AlertDetailPage is unmounted (BRIEF-E5d): /client/alert/:id redirects home.
import { DashboardPage } from './pages/DashboardPage'
// OrgDashboard is intentionally unmounted (DW-ORG-DIES-TEAM-HOME). Customers
// never see /org; the file stays in the tree for a later audited cleanup.
import { AlertsPage } from './pages/AlertsPage'
import { ConsolePage } from './pages/ConsolePage'
import { ConsoleReadoutPage } from './pages/ConsoleReadoutPage'
import { ConsoleSupportViewPage } from './pages/ConsoleSupportViewPage'
import { TeamKnowledgePage } from './pages/TeamKnowledgePage'
import { TeamQuestionsPage } from './pages/TeamQuestionsPage'
import { EscalationPrefsPage } from './pages/EscalationPrefsPage'
import { OnboardingInterviewPage } from './pages/OnboardingInterviewPage'
import { ConsentCallbackPage } from './pages/ConsentCallbackPage'
import { ConsentResultPage } from './pages/ConsentResultPage'
import { SmbVerifyPage } from './pages/SmbVerifyPage'
import { SmbSignupPage } from './pages/SmbSignupPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { StaffNoticePage } from './pages/StaffNoticePage'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ClientInterviewGate } from './components/ClientInterviewGate'

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Set-password landing from the forgot-password email — PUBLIC (the
            visitor holds only the emailed token, no session). Without this
            route the catch-all swallowed the link and dropped the token. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* M365 admin-consent landing (DW-FE-M365-CALLBACK / BLIND-1).
            Microsoft's registered redirect_uri points at this FRONTEND path;
            the callback forwards the query string to the backend, which does
            the real work and 302s back to the success/error pages below.
            All PUBLIC — the consenting customer admin need not be a logged-in
            app user, so these must not sit behind ProtectedRoute. */}
        <Route
          path="/onboarding/microsoft/consent-callback"
          element={<ConsentCallbackPage />}
        />
        <Route
          path="/onboarding/success"
          element={<ConsentResultPage variant="success" />}
        />
        <Route
          path="/onboarding/error"
          element={<ConsentResultPage variant="error" />}
        />
        {/* SMB self-serve flow shares the same redirect URI; its own landings */}
        {/* Self-serve signup entry — PUBLIC (the signer-up has no account
            yet). Without this route the catch-all bounced /smb to login and
            a second company could never start the flow on its own. */}
        <Route path="/smb" element={<SmbSignupPage />} />

        {/* Email-verification landing from the signup email — PUBLIC (the
            signer-up has no app account yet). Without this route the
            catch-all swallowed the link and dropped the tokens. */}
        <Route path="/smb/verify" element={<SmbVerifyPage />} />
        <Route
          path="/smb/consent-complete"
          element={<ConsentResultPage variant="success" />}
        />
        <Route
          path="/smb/consent-failed"
          element={<ConsentResultPage variant="error" />}
        />

        {/* Unified dashboard entry — redirects by role */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Client routes. /client is the customer page (BRIEF-E2b), fed by
            /api/v1/soc/; it hands a place handoff or an owed first meeting
            to ClientDashboard, which keeps those. */}
        <Route
          path="/client"
          element={
            <ProtectedRoute>
              <CustomerHomePage />
            </ProtectedRoute>
          }
        />
        {/* Side-door routes: incomplete first meeting must not open SIEM/chat/settings.
            ProtectedRoute stays outside; ClientInterviewGate holds these plus
            /client/interview itself.
            /client, /client/alerts, /client/briefing already gate inside ClientDashboard.
            Status is read-only — never start a meeting here. */}
        {/* BRIEF-E5d: the old alert pages are not customer pages; both
            redirect home, where the one customer header starts. */}
        <Route path="/client/alert/:id" element={<Navigate to="/client" replace />} />
        <Route path="/client/alerts" element={<Navigate to="/client" replace />} />
        {/* BRIEF-B10: the owner tells their staff, under the portal's own
            sign-in. Outside ClientInterviewGate: it is a sign-up step, before
            the first meeting. The route itself refuses anyone but the owner. */}
        <Route
          path="/client/staff-notice"
          element={
            <ProtectedRoute>
              <StaffNoticePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/briefing"
          element={
            <ProtectedRoute>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        {/* The Concierge — the client "talks to" their security team in plain
            language (streaming chat over SSE). */}
        <Route
          path="/client/concierge"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <ConciergePage />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />
        {/* "Your team is asking" — Context-Keeper relationship questions */}
        <Route
          path="/client/questions"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <TeamQuestionsPage />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />
        {/* Tripwires (BRIEF-E2b): name, download, say where, confirm. */}
        <Route
          path="/client/tripwires"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <TripwiresPage />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />

        {/* Onboarding interview — "the first meeting with your new security
            lead". The client conducts THEIR OWN onboarding, one question at a
            time, in the Concierge's voice. Its own client-facing page (the
            admin onboarding wizard at /onboarding is a separate surface).

            Gated like every other client side door. This route used to mount
            bare: a household that landed here from a bookmark or a leftover
            link (an incomplete or unknown-status first meeting) got the
            interview questions and never reached /client home — so it never
            saw HomeTripwiresCard and never saw "Download Decoy (.docx)". That
            made the first meeting a second product that hides the canary.
            DigitalWatch.md: canaries are in the same picture — a tripwire the
            analyst sees with the logs. So incomplete/unknown/status-throw
            leaves to /client via the gate, where ClientDashboard already keeps
            the meeting on home WITH the Word decoy download in that home
            section. A complete client still paints the interview page, and the
            SIEM hold is unchanged: an incomplete meeting never paints the
            questions from this route either. */}
        <Route
          path="/client/interview"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <OnboardingInterviewPage />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />

        {/* "What your team knows about your world" — the client's OWN
            patterns-of-life read-back and the correction affordances
            ("That's expected" / "Correct this" / "Remove"). Same page the
            analyst sees at /analyst/knowledge, in the client shell
            (DESIGN_DECISION_client_home_v2 §1.e). Behind the client
            ProtectedRoute + interview gate like every other side door: an
            unfinished first meeting has nothing written to read back yet. */}
        <Route
          path="/client/world"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <TeamKnowledgePage audience="client" />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />

        {/* Escalation & contact preferences — "how your team reaches you"
            (who gets woken, for what, and how). The client edits their OWN
            profile via GET/PUT /escalation/profile. A settings-style page. */}
        <Route
          path="/client/settings/escalation"
          element={
            <ProtectedRoute>
              <ClientInterviewGate>
                <EscalationPrefsPage />
              </ClientInterviewGate>
            </ProtectedRoute>
          }
        />

        {/* /org is not a customer surface (Steve 26 Aug 2026). Permanent redirect
            to team home. OrgDashboard.tsx stays in the tree, unmounted (F-08). */}
        <Route path="/org" element={<Navigate to="/client" replace />} />

        {/* SOC lead console (BRIEF-F1b): the staff landing page. Staff only:
            ProtectedRoute sends a customer principal to /client and every
            /hub/console/ route re-checks staff server-side. */}
        <Route
          path="/console"
          element={
            <ProtectedRoute requiredRole="analyst">
              <ConsolePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/console/business/:tenantId"
          element={
            <ProtectedRoute requiredRole="analyst">
              <ConsoleReadoutPage />
            </ProtectedRoute>
          }
        />
        {/* BRIEF-F3: a business's page as its owner sees it. Read-only;
            every /as-owner/ call re-checks staff and is logged server-side. */}
        <Route
          path="/console/business/:tenantId/as-owner"
          element={
            <ProtectedRoute requiredRole="analyst">
              <ConsoleSupportViewPage />
            </ProtectedRoute>
          }
        />

        {/* Analyst routes */}
        <Route
          path="/analyst"
          element={
            <ProtectedRoute requiredRole="analyst">
              <AnalystDashboard />
            </ProtectedRoute>
          }
        />
        {/* Human-analyst escalation view — read-only, full technical detail */}
        <Route
          path="/analyst/escalations"
          element={
            <ProtectedRoute requiredRole="analyst">
              <EscalationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analyst/case/:id"
          element={
            <ProtectedRoute requiredRole="analyst">
              <CaseDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analyst/onboard"
          element={
            <ProtectedRoute requiredRole="analyst">
              <OnboardFamilyPage />
            </ProtectedRoute>
          }
        />

        {/* Patterns-of-life read-back — "what your team knows about your
            world", analyst side. The same page also mounts for the client at
            /client/world; the backing GET is tenant-scoped, not staff-only. */}
        <Route
          path="/analyst/knowledge"
          element={
            <ProtectedRoute requiredRole="analyst">
              <TeamKnowledgePage />
            </ProtectedRoute>
          }
        />

        {/* Canonical case route (no role prefix) */}
        <Route
          path="/cases/:id"
          element={
            <ProtectedRoute requiredRole="analyst">
              <CaseDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Alerts page — analyst view with full filtering */}
        <Route
          path="/alerts"
          element={
            <ProtectedRoute requiredRole="analyst">
              <AlertsPage />
            </ProtectedRoute>
          }
        />

        {/* /tenants was a list analysts could not load (it read an admin-only
            route). Every business is on the SOC lead console now (BRIEF-F1b);
            suspend, resume, export and offboard stay on /admin. */}
        <Route path="/tenants" element={<Navigate to="/console" replace />} />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
